import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import { Box, Card, ListItem, Tooltip } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import Typography from "@mui/material/Typography";
import { map, take } from "ramda";
import { useSearchQuery } from "../../creator-application/common/use-searchQuery";
import { useSearch } from "../../creator-application/searchServices";
import { notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../PanelLoadingIndicator";
import ListItemText from "@mui/material/ListItemText";
import List from "@mui/material/List";
import Button from "@mui/material/Button";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Link } from "react-router-dom";
import { calculateDate, dateRanges } from "./DateRangeSelect";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
    minHeight: 390,
    height: "100%",
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    borderRadius: 20,
    color: theme.palette.secondary.contrastText,
    display: "flex",
    justifyContent: "flex-start",
    flexDirection: "column",
  },

  h1: {
    fontSize: "3.6rem",
    lineHeight: "3.4rem",
    fontWeight: "bold",
    color: theme.palette.secondary.contrastText,
  },

  h4: {
    color: theme.palette.secondary.contrastText,
  },

  listItem: {
    borderBottomStyle: "dashed",
    borderBottomColor: theme.palette.secondary.contrastText,
    borderBottomWidth: 1,
  },

  listItemText: {
    fontSize: "1.6rem",
    lineHeight: "3.4rem",
    color: theme.palette.secondary.contrastText,
    textTransform: "none",
  },

  progressWrapper: {
    flexGrow: 1,
  },

  moduleList: {
    padding: 0,
  },

  allButton: {
    fontSize: "1.4rem",
    lineHeight: "1.8rem",
    fontWeight: "bold",
    paddingTop: theme.spacing(2),
    color: theme.palette.secondary.contrastText,
  },

  forwardIcon: {
    color: theme.palette.secondary.contrastText,

    "& :first-child": {
      fontSize: "1.4rem",
      lineHeight: "1.8rem",
      fontWeight: "bold",
      color: theme.palette.secondary.contrastText,
    },
  },

  body1: {
    fontStyle: "italic",
    fontSize: "1.6rem",
    lineHeight: "2rem",
    color: theme.palette.secondary.contrastText,
  },
}));

const initialDate = calculateDate(dateRanges.last_14);
const initialSearchState = {
  appliedQuery: [],
  appliedFacets: [
    {
      name: "type",
      queryParam: "_type",
      terms: [
        {
          code: "MODULE",
        },
      ],
    },
  ],
  orderBy: "recordDate",
  orderDirection: "desc",
  otherParams: [{ au: `${initialDate}|` }],
  appliedPublicationYearGroups: [],
  limit: 4,
  page: 0,
  noFacets: true,
};

const RecentModulesCard = () => {
  const classes = useStyles();

  //-- Handle Search state
  const { state } = useSearchQuery(initialSearchState);

  const searchResults = useSearch(state);

  const [modules, setModules] = useState([]);
  useEffect(() => {
    if (searchResults.isSuccess && notBlank(searchResults.data.media)) {
      setModules(take(4, searchResults.data.media));
    } else {
      setModules([]);
    }
  }, [searchResults.isSuccess, searchResults.data, state]);

  return (
    <Card classes={{ root: classes.root }}>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Typography variant="h4" component="div" classes={{ h4: classes.h4 }}>
            Recently Updated Modules
          </Typography>
        </Grid>
      </Grid>
      <Box>
        <Typography variant="h1" component="span" classes={{ h1: classes.h1 }}>
          {searchResults.isSuccess
            ? searchResults.data.media.length.toLocaleString()
            : "Loading..."}
        </Typography>
      </Box>
      {searchResults.isSuccess ? (
        notBlank(modules) ? (
          <Box>
            <List classes={{ root: classes.moduleList }}>
              {map(
                module => (
                  <Link
                    key={module.id}
                    to={{
                      pathname: `/creator/modules/${module.id}`,
                    }}
                    state={{ from: "home" }}
                  >
                    <ListItem
                      disableGutters
                      classes={{ root: classes.listItem }}
                    >
                      <Tooltip title={module.name} placement="bottom-start">
                        <ListItemText
                          primaryTypographyProps={{ noWrap: true }}
                          classes={{ primary: classes.listItemText }}
                          primary={module.name}
                        />
                      </Tooltip>
                    </ListItem>
                  </Link>
                ),
                modules,
              )}
            </List>
          </Box>
        ) : (
          <Box
            width={165}
            height={65}
            ml={"auto"}
            mr={"auto"}
            mt={12}
            textAlign="center"
          >
            <Typography
              component="div"
              variant="body1"
              classes={{ body1: classes.body1 }}
            >
              No modules have been updated in the last 2 weeks
            </Typography>
            <Button
              size="small"
              classes={{
                textSizeSmall: classes.allButton,
                iconSizeSmall: classes.forwardIcon,
              }}
              variant="text"
              color="primary"
              endIcon={<ArrowForwardIcon fontSize="small" />}
            >
              View all Modules
            </Button>
          </Box>
        )
      ) : (
        <Box flexGrow={1}>
          <PanelLoadingIndicator color="secondary" />
        </Box>
      )}
    </Card>
  );
};

export default RecentModulesCard;
